/* eslint-disable camelcase */
import { createSelector } from 'reselect';
import moment from 'moment';
import { shelfIOSPeriods, shelfPeriods, shelfSkanModeledCohortPeriods } from 'reducers/reportsConfig/shelfPeriods';
import { reportDataToChartData } from 'utils/charts';
import {
    DEFAULT_APP_FILTER,
    DEFAULT_SOURCE_FILTER,
    defaultLastFilter,
    getDrilldownDimensions,
    isPostProcessFilterField,
    listContains,
    reportTypes,
    transparencyReportTypes,
} from 'utils/reports';
import {
    ACCOUNT_TYPES,
    COHORT_PERIODS_THRESHOLD,
    CreativeTypesMapping,
    CUSTOMER_TIERS,
    ReportFieldTypes,
} from 'utils/consts';
import {
    getGridFilteredData,
    getTableDataWithTransparency,
    isFacebookCensored,
    reportDataToTableData,
} from 'utils/grids';
import { showCohortEvents, showConversionEvents } from 'customEvents/utils';
import {
    COHORT_METRICS_STANDARD_ANALYTICS_TOOLTIP,
    IN_OPERATOR,
    NOT_IN_OPERATOR,
    PUBLISHER_ESTIMATED_INDICATION,
    SOURCE_FIELD,
} from 'utils/reportsConsts';
import { COMPARE_MODE, DATE_FIELDS, REGULAR_MODE } from 'components/pages/reports/consts';
import { sourceTagMatchesValue, NETWORKS_SPECIAL_ORIGINAL_DISPLAY_NAME_MAPPING } from 'utils/adNetworks';
import { getAccountType, getFields, getFieldsForType, getFilters } from './fields';
import { getAdminModeEnabled, getUserData, isUserAdNetwork } from './user';
import fieldVisibilityRules from './reportsConfig/fieldVisibilityRules';
import reportsConfig from './reportsConfig';
import { API_VERSIONS } from '../customDashboards/utils';
import fieldDefaultRules from './reportsConfig/fieldDefaultingRules';
import { getCanAccessFeature } from './navigation';

const TIME_BREAKDOWNS = {
    DAY: 'day',
    WEEK: 'week',
    MONTH: 'month',
    ALL: 'all',
};

const CHART_OPTIMIZATION_DATE_RANGES = {
    WEEKLY_THRESHOLD: 30, // 1 month
    MONTHLY_THRESHOLD: 180, // 6 months
};

const getChartTimeBreakdown = (startDate, endDate, currentBreakdown) => {
    const daysDiff = moment(endDate).diff(moment(startDate), 'days');

    if (daysDiff > CHART_OPTIMIZATION_DATE_RANGES.MONTHLY_THRESHOLD && currentBreakdown === TIME_BREAKDOWNS.DAY) {
        return TIME_BREAKDOWNS.MONTH;
    }

    if (daysDiff > CHART_OPTIMIZATION_DATE_RANGES.WEEKLY_THRESHOLD && currentBreakdown === TIME_BREAKDOWNS.DAY) {
        return TIME_BREAKDOWNS.WEEK;
    }

    return currentBreakdown;
};

const arrangeFilters = array => {
    const filtered_array = array.filter(element => !element.tagDivider);

    const dimensions = [];
    const custom_dimensions = [];

    // throw the elements into buckets
    filtered_array.forEach(element => {
        let bucket = dimensions;

        // IMPORTANT: the order of the if's here is critical !!!
        // because a tag group is also a dynamic dimension
        if (element.dynamic_dimension) {
            bucket = custom_dimensions;
        }

        bucket.push(element);
    });

    // prepare the headers
    const dimensions_headers = {
        creative_visible: true,
        display_name: 'STATIC.DIMENSIONS',
        divider: true,
        tagDivider: true,
        name: 'dimensions_divider',
    };

    const custom_dimensions_header = {
        creative_visible: true,
        display_name: 'STATIC.CUSTOM_DIMENSIONS',
        divider: true,
        tagDivider: true,
        name: 'custom_dimensions_divider',
    };

    // prepare the final dimension list
    let final_dimensions = [];

    if (dimensions.length > 0) {
        final_dimensions.push(dimensions_headers);
        const sorted_dimensions = [...dimensions];
        sorted_dimensions.sort((a, b) => a.display_name.localeCompare(b.display_name));
        final_dimensions = final_dimensions.concat(sorted_dimensions);
    }

    if (custom_dimensions.length > 0) {
        final_dimensions.push(custom_dimensions_header);
        const sorted_custom_dimensions = [...custom_dimensions];
        sorted_custom_dimensions.sort((a, b) => a.display_name.localeCompare(b.display_name));
        final_dimensions = final_dimensions.concat(sorted_custom_dimensions);
    }

    // splice the source array
    array.splice(0, array.length);
    array.push(...final_dimensions);
};

const basicSelectors = [
    'selectedFields',
    'filters',
    'shelf',
    'header',
    'showFieldsSelection',
    'showSpinner',
    'showHeader',
    'selectedTab',
    'dates',
    'fieldWarnings',
    'fieldShowDownloadReport',
    'showDownloadReportDatesThreshold',
    'chartData',
    'reportData',
    'chart',
    'table',
    'anonymousId',
    'anonymousList',
    'reportWarning',
    'bookmark',
    'selectedGoal',
    'mode',
    'transparencyData',
    'queryRunning',
    'validationError',
    'validationErrorData',
    'unenriched',
    'admonAlignment',
    'displayAlignment',
    'showRedownloadsDimensionInReport',
    'showValidatedDimensionInReport',
    'shouldDrilldown',
    'postProcessFiltersUI',
    'isSlimMode',
    'isClearSelectionOn',
    'disableReportState',
    'openFieldsAvailability',
    'fieldsAvailabilityFieldName',
    'selectedSources',
    'selectedApps',
    'showPopupDialog',
    'reportFormHasChanged',
    'isEditShelfOpen',
    'isAdvanceMode',
    'hasSearchFilter',
];

const getConfigForType = type => {
    return reportsConfig.find(conf => {
        return conf.type === type;
    });
};

// initializes the basic selectors
reportsConfig.forEach(reportType => {
    basicSelectors.forEach(basic => {
        if (!reportType.selectors) {
            reportType.selectors = {};
        }
        reportType.selectors[basic] = state => {
            return state[reportType.type][basic];
        };
    });
});

export const getReportCheckedFields = (type, reportType) => {
    let reportTypeObj = reportType;

    if (typeof reportType === 'string') {
        reportTypeObj = getConfigForType(reportType);
    }

    return createSelector(
        [
            getFieldsForType(type),
            reportTypeObj.selectors.selectedFields,
            reportTypeObj.selectors.isSlimMode,
            reportTypeObj.selectors.isAdvanceMode,
            reportTypeObj.selectors.selectedSources,
            reportTypeObj.selectors.hasSearchFilter,
            getAdminModeEnabled,
            getUserData,
        ],
        (
            fields,
            selectedFields,
            isSlimMode,
            isAdvanceMode,
            selectedSources,
            hasSearchFilter,
            adminModeEnabled,
            userData
        ) => {
            const isStandardAnalytics = userData.organization_tier === CUSTOMER_TIERS.STANDARD_ANALYTICS;

            if (isStandardAnalytics) {
                selectedFields.cohortMetrics = [];
            }

            return (fields || [])
                .filter(field => {
                    const isFieldSelected = (selectedFields[field.actual_type] || []).includes(field.name);
                    if (isFieldSelected && !fieldDefaultRules.force(field)) return true;

                    const params = [field, adminModeEnabled, userData, isSlimMode];
                    if (reportType.type === reportTypes.creativeExplore) {
                        params.push(isAdvanceMode, selectedSources);
                    }

                    params.push(hasSearchFilter);

                    return reportTypeObj.visibleRule(...params);
                })
                .map(field => ({
                    ...field,
                    tooltip:
                        field.actual_type === 'cohortMetrics' && isStandardAnalytics
                            ? COHORT_METRICS_STANDARD_ANALYTICS_TOOLTIP
                            : field.tooltip,
                    checked: !!selectedFields[field.actual_type]?.includes(field.name),
                }));
        }
    );
};

const getReportDates = reportType => {
    let reportTypeObj = reportType;
    if (typeof reportType === 'string') {
        reportTypeObj = getConfigForType(reportType);
    }
    const datesSelector = reportTypeObj.selectors.dates;
    return createSelector([getFieldsForType('dates'), datesSelector], (fieldDates, reportDates) => {
        if (!reportDates.end_date || !reportDates.start_date) {
            return fieldDates;
        }
        return reportDates;
    });
};

const getChartFormattedData = reportType => {
    return createSelector([reportType.selectors.chartData, getUserData], (chartData, userData) => {
        if (!chartData || !Object.keys(chartData).length) {
            return { metrics: [], dimensions: {}, orgOptions: {} };
        }

        if (chartData.results) {
            chartData.results = chartData.results
                .map(chartDataRow => (isFacebookCensored(chartDataRow) ? null : chartDataRow))
                .filter(chartDataRow => chartDataRow !== null);
        }

        const normalizedChartData = reportDataToChartData(chartData);
        normalizedChartData.orgOptions = {
            metricsLimit: 3,
        };
        // if chart_metrics_display exits in user data, map the chart type to the relevant metric
        if (userData) {
            if (userData.chart_metrics_display) {
                try {
                    const parsedChartMetricsDisplay = JSON.parse(userData.chart_metrics_display);
                    for (const metric of normalizedChartData.metrics) {
                        metric.chart_type = parsedChartMetricsDisplay[metric.name] || 'line';
                    }
                } catch (e) {}
            }
            normalizedChartData.orgOptions = {
                metricsLimit: userData.chart_metrics_limit || normalizedChartData.orgOptions.metricsLimit,
            };
        }
        return normalizedChartData;
    });
};

const getTableFormattedData = reportType => {
    let reportTypeObj = reportType;
    if (typeof reportType === 'string') {
        reportTypeObj = getConfigForType(reportType);
    }
    const reportDataSelector = reportTypeObj.selectors.reportData;
    const tableSelector = reportTypeObj.selectors.table;
    return createSelector(
        [
            reportDataSelector,
            tableSelector,
            reportTypeObj.selectors.shouldDrilldown,
            getUserData,
            reportTypeObj.selectors.postProcessFiltersUI,
            isUserAdNetwork,
        ],
        (reportData, table, shouldDrilldown, userData, postProcessFiltersUI, isAdNetwork) => {
            let mode = REGULAR_MODE;
            let showConfidenceInterval = false;
            if (table && table.request) {
                mode = table.request.compare ? COMPARE_MODE : mode;
                mode = table.request.is_goals ? 'goal' : mode;
                showConfidenceInterval =
                    table.request.confidence_interval_flag && table.request.confidence_interval_flag.includes('true');
            }
            const showDimensionDrilldownIcon = reportTypeObj.type === reportTypes.mainReport && mode !== 'goal';

            let tableData = reportData
                ? reportDataToTableData(
                      reportType.type,
                      reportData,
                      mode,
                      shouldDrilldown,
                      showDimensionDrilldownIcon,
                      table.column_order || [],
                      postProcessFiltersUI,
                      showConfidenceInterval,
                      null,
                      reportType,
                      null, // reportDates
                      null // widgetQuery
                  )
                : {};

            if (reportData && reportTypeObj.transformTableData) {
                tableData = reportTypeObj.transformTableData(tableData);
            }

            return {
                ...tableData,
                reportId: reportData ? reportData.report_id : null,
                cacheTimestamp:
                    reportData && reportData.cache_timestamp
                        ? moment(reportData.cache_timestamp * 1000).format('lll:s')
                        : 'NOW',
                attributionTimezone:
                    userData && userData.impersonated_org_attribution_timezone
                        ? userData.impersonated_org_attribution_timezone
                        : userData.attribution_timezone,
                customer:
                    isAdNetwork && userData && userData.impersonated_org_company_display_name
                        ? userData.impersonated_org_company_display_name
                        : null,
                tiAttributionMigrationDate: userData && userData.ti_attribution_migration_date,
                dpouMigrationDates: userData && userData.dpou_migration_dates,
                snowflakeMigrationTime: userData && userData.snowflake_migration_time,
                useCalendricCohort: userData && userData.use_calendric_cohort,
                postProcessFiltersUI,
            };
        }
    );
};

const getTransparencyData = reportType => {
    return createSelector(
        [reportType.selectors.tableDataFormatted, reportType.selectors.transparencyData],
        (tableData, transparencyData) => {
            return transparencyData &&
                tableData.columnDefs &&
                tableData.columnDefs.find(field => field.field === 'source')
                ? getTableDataWithTransparency(tableData, transparencyData, reportType.type)
                : tableData || {};
        }
    );
};

const getShelfShown = reportType => {
    let reportTypeObj = reportType;

    if (typeof reportType === 'string') {
        reportTypeObj = getConfigForType(reportType);
    }

    return createSelector([reportTypeObj.selectors.showFieldsSelection], showFieldsSelection => showFieldsSelection);
};

const getPeriods = (periods, reportType) => {
    return periods.filter(period => fieldVisibilityRules[reportType](period));
};

function updatePeriodsViewConfig(periodsItem, periods, reportType) {
    if (periodsItem) {
        // In case there are more cohort periods than the threshold, show them as tags.
        if (getPeriods(periods, reportType.type).length > COHORT_PERIODS_THRESHOLD) {
            periodsItem.group.type = 'tag';
            periodsItem.group.data.placeholder = 'STATIC.PLACEHOLDERS.SELECT_PERIOD';
        } else {
            periodsItem.group.type = 'multi';
            delete periodsItem.group.data.placeholder;
        }
    }
}

const getShelfFormatted = reportType => {
    return createSelector(
        [
            reportType.selectors.selectedTab,
            reportType.selectors.shelf,
            getFieldsForType('periods'),
            getFieldsForType('skanModeledCohortPeriods'),
            getFieldsForType('unifiedCohortPeriods'),
            isUserAdNetwork,
            getCanAccessFeature('singularevents'),
        ],
        (
            selectedTab = 'regular',
            shelf,
            periods,
            skanPeriods,
            combinedPeriods,
            isAdnetwork,
            canAccessEventsFeature
        ) => {
            let ret = shelf;
            if (typeof shelf === 'object' && !(shelf instanceof Array)) {
                // if the user is of partner org, we need to show different shelf options
                if (isAdnetwork && selectedTab === 'regular' && shelf.partner) {
                    ret = shelf.partner;
                } else {
                    ret = shelf[selectedTab];
                }
            }

            const periodsItem = ret.find(item => item.type === shelfPeriods.type);
            const skanPeriodsItem = ret.find(item => item.type === shelfSkanModeledCohortPeriods.type);
            const unifiedCohortPeriodsItem = ret.find(item => item.type === shelfIOSPeriods.type);

            updatePeriodsViewConfig(periodsItem, periods, reportType);
            updatePeriodsViewConfig(skanPeriodsItem, skanPeriods, reportType);
            updatePeriodsViewConfig(unifiedCohortPeriodsItem, combinedPeriods, reportType);

            if (!canAccessEventsFeature) {
                ret = ret.map(item =>
                    item.type === 'conversionEvents' && item.addButton?.show
                        ? { ...item, addButton: { ...item.addButton, show: false } }
                        : item
                );
            }

            return ret;
        }
    );
};

// A selector to format the fields for an api query
const formattedFilters = reportType =>
    createSelector([reportType.selectors.filters, getFilters], (storeFilters, fieldsFilters) =>
        storeFilters
            .filter(filter => filter.field.name !== 'default')
            .map(filter => {
                const fFilter = fieldsFilters.find(ff => ff.name === filter.field);
                const isSourceField = filter.field === SOURCE_FIELD;
                return {
                    dimension: filter.field,
                    operator: filter.operator,
                    values: filter.tags.map(tag => {
                        // Resolve a stored tag back to its value name. For source, match the machine
                        // name, the raw backend display_name (legacy/saved reports) or the central
                        // mapped display name; other dimensions keep a strict display_name match.
                        // Shares one helper with getCalcFilterForType so they stay in sync.
                        const filterObj =
                            fFilter &&
                            fFilter.values &&
                            fFilter.values.find(value => sourceTagMatchesValue(value, tag, isSourceField));
                        return filterObj ? filterObj.name : tag;
                    }),
                    options: { no_lowercase: true },
                    isPostProcess: filter.isPostProcess,
                };
            })
    );

/**
 * Optimizes filters based on selection percentage:
 * - If only one value is selected: no optimization (keep original filter)
 * - If all options are selected: filter is excluded (no filter applied)
 * - If less than 50% selected: use "include" operator with selected values
 * - If more than 50% selected: use "exclude" operator with unselected values
 */
const optimizeFilters = (filters, fieldsFilters) => {
    return filters
        .filter(({ values }) => values.length)
        .map(filter => {
            const { operator, dimension } = filter;
            const otherOperator = operator === IN_OPERATOR ? NOT_IN_OPERATOR : IN_OPERATOR;

            const filterField = fieldsFilters.find(({ name }) => name === dimension);

            if (!filterField?.values?.length) return filter;

            const totalAvailableValues = filterField.values.length;
            const selectedValuesCount = filter.values.length;

            // If only one value is selected, no optimization needed
            if (selectedValuesCount === 1) return filter;

            // If all options are selected, exclude this filter (no filtering needed)
            if (selectedValuesCount === totalAvailableValues) {
                return operator === IN_OPERATOR ? null : filter;
            }

            const selectionPercentage = selectedValuesCount / totalAvailableValues;

            // If 50% or less selected, use include operator with selected values
            if (selectionPercentage <= 0.5) {
                return filter;
            }

            // If more than 50% selected, use exclude operator with unselected values
            const selectedValueNames = new Set(filter.values);
            const unselectedValues = filterField.values
                .map(value => value.name)
                .filter(valueName => !selectedValueNames.has(valueName));

            return {
                ...filter,
                operator: otherOperator,
                values: unselectedValues,
            };
        })
        .filter(filter => !!filter); // Remove null filters (100% selected)
};

const getShowReportMeta = () =>
    createSelector([getAdminModeEnabled], adminModeEnabled => {
        return adminModeEnabled;
    });

// initialize the advanced selectors
reportsConfig.forEach(reportType => {
    reportType.fields.forEach(field => {
        reportType.selectors[field] = getReportCheckedFields(field, reportType);
    });
    reportType.selectors.reportDates = getReportDates(reportType);
    reportType.selectors.tableDataFormatted = getTableFormattedData(reportType);
    if (transparencyReportTypes.includes(reportType.type)) {
        reportType.selectors.transparencyDataFormatted = getTransparencyData(reportType);
    }
    reportType.selectors.chartDataFormatted = getChartFormattedData(reportType);
    reportType.selectors.shelfFormatted = getShelfFormatted(reportType);
    reportType.selectors.formattedFilters = formattedFilters(reportType);
    reportType.selectors.showReportMeta = getShowReportMeta();
});

const getAvailableDimensionsForDrilldown = () => {
    return createSelector(
        [getConfigForType(reportTypes.mainReport).selectors.reportData, getUserData],
        (reportData, userData) => {
            const { metadata = {} } = reportData;
            const isStandardAnalytics = userData.organization_tier === CUSTOMER_TIERS.STANDARD_ANALYTICS;

            const availableDrilldownDimensions = getDrilldownDimensions(metadata).filter(({ name, values = [] }) => {
                return !(metadata[name] || values.find(value => metadata[value]));
            });

            if (isStandardAnalytics) {
                return availableDrilldownDimensions.filter(dimension => dimension.standardAnalyticsVisible);
            }

            return availableDrilldownDimensions;
        }
    );
};

const cacheSelectorByType = factory => {
    const selectorsByType = {};
    return type => {
        if (!(type in selectorsByType)) {
            selectorsByType[type] = factory(type);
        }
        return selectorsByType[type];
    };
};

const getAvailableFiltersForType = cacheSelectorByType(type => {
    const reportType = reportsConfig.find(rType => {
        return type === rType.type;
    });
    return createSelector(
        [
            getFilters,
            reportType.selectors.filters,
            getAdminModeEnabled,
            getUserData,
            reportType.selectors.isSlimMode,
            reportType.selectors.hasSearchFilter,
        ],
        (filters, reportFilters, adminModeEnabled, userData, isSlimMode, hasSearchFilter) => {
            const filteredFilters = filters.filter(
                filter =>
                    reportType.visibleRule(filter, adminModeEnabled, userData, isSlimMode, hasSearchFilter) &&
                    !reportFilters.find(cFilter => {
                        return cFilter.field === filter.name;
                    })
            );
            arrangeFilters(filteredFilters);
            return filteredFilters;
        }
    );
});

const getCalcFilterForType = cacheSelectorByType(type => {
    const reportType = reportsConfig.find(rType => {
        return type === rType.type;
    });
    return createSelector(
        [reportType.selectors.filters, getFilters, getFieldsForType('filterOperators'), getAccountType],
        (reportFilters, fieldsFilters, filterOperators, accountType) => {
            const isStandardAnalytics = window.organization_tier === CUSTOMER_TIERS.STANDARD_ANALYTICS;
            const isAdmonReport = type === 'adMonetization';
            const isPartner = accountType === ACCOUNT_TYPES.PARTNER;

            // When there is an exception with the all_fields route, this might happen.
            if (!filterOperators) {
                return null;
            }

            return reportFilters
                .filter(
                    cFilter =>
                        !(isStandardAnalytics && cFilter.field === DEFAULT_APP_FILTER.field) &&
                        !(isAdmonReport && cFilter.field === DEFAULT_SOURCE_FILTER.field) &&
                        !(isPartner && cFilter.field === DEFAULT_SOURCE_FILTER.field)
                )
                .map(cFilter => {
                    const ret = { ...defaultLastFilter };
                    if (
                        fieldsFilters &&
                        cFilter.field !== defaultLastFilter.field.name &&
                        cFilter.field !== defaultLastFilter.field
                    ) {
                        ret.field = fieldsFilters.find(
                            fFilter => fFilter.name === cFilter.field || fFilter.name === cFilter.field.name
                        );
                    }
                    const availableOperators = ret.field?.operators || filterOperators;
                    ret.operator = availableOperators.find(operator => operator.name === cFilter.operator);
                    ret.isPostProcess = cFilter.isPostProcess;
                    if (cFilter.tags && cFilter.tags.length && ret.field) {
                        if (ret.field.values) {
                            // Resolve a stored tag back to its value object, mirroring formattedFilters
                            // via the shared helper. For source this matches the machine name, raw
                            // display_name, or central mapped display name (e.g. 'Facebook (Meta Ads)')
                            // so source tags don't silently drop; other dimensions stay strict.
                            const isSourceField = ret.field.name === SOURCE_FIELD;
                            ret.tags = cFilter.tags
                                .map(tag =>
                                    ret.field.values.find(value => sourceTagMatchesValue(value, tag, isSourceField))
                                )
                                .filter(tag => tag !== undefined);
                        } else {
                            ret.tags = cFilter.tags.map(tag => ({ name: tag, display_name: tag }));
                        }
                    }
                    return ret;
                });
        }
    );
});

const shouldShowDownloadReport = (reportType, type) => {
    const isDimensions = type === 'dimensions';
    return createSelector(
        [
            getConfigForType(reportType).selectors[type],
            getConfigForType(reportType).selectors.fieldShowDownloadReport,
            getConfigForType(reportType).selectors.showDownloadReportDatesThreshold,
            getReportDates(reportType),
        ],
        (fields, fieldShowDownloadReport, showDownloadReportDatesThreshold, reportDates) => {
            if (
                isDimensions &&
                moment(reportDates.end_date).diff(reportDates.start_date, 'day') > showDownloadReportDatesThreshold
            ) {
                return true;
            }
            if (Array.isArray(fields)) {
                for (const field of fields) {
                    if (field.checked && fieldShowDownloadReport[field.name]) {
                        return true;
                    }
                }
            }
            return false;
        }
    );
};

const getWarningForType = (reportType, type) => {
    const { selectors } = getConfigForType(reportType);
    return createSelector(
        [selectors[type], selectors.fieldWarnings, selectors.filters, selectors.selectedFields],
        (fields, fieldWarnings, filters, selectedFields) => {
            // get field warning is not implemented for non-fieldGroup fields (the only case where fields will be an array)
            // It should be simple to add, though
            if (!Array.isArray(fields)) {
                return [];
            }

            const warnings = [
                ...new Set(
                    fields
                        .filter(field => field.checked && fieldWarnings[field.name])
                        .map(field => fieldWarnings[field.name].msg)
                ),
            ].map(message => ({ message }));

            if (type === 'dimensions') {
                const selectedDimensions = new Set(selectedFields.dimensions || []);
                const missingFilterDimensions = filters
                    .filter(f => f.isPostProcess && f.tags?.length && !selectedDimensions.has(f.field))
                    .map(f => fields.find(field => field.name === f.field)?.display_name || f.field);
                if (missingFilterDimensions.length) {
                    warnings.push({
                        message: 'STATIC.PAGES.REPORTS.FREE_TEXT_FILTER_NEEDS_DIMENSION',
                        data: { dimensions: [...new Set(missingFilterDimensions)].join(', ') },
                    });
                }
            }

            return warnings;
        }
    );
};

function _buildValidPermutations(request, chartRefRows, chart) {
    request.valid_key_list_for_charts = [];
    request.valid_key_name_list_for_charts = [];

    let isKeyNameListExists = false;
    chartRefRows.forEach(row => {
        const permutation = [];
        request.permutation_keys.forEach(field => {
            permutation.push(encodeURIComponent(row[field.name]));
            if (!isKeyNameListExists) {
                request.valid_key_name_list_for_charts.push(field.name);
            }
        });
        if (!listContains(request.valid_key_list_for_charts, permutation)) {
            request.valid_key_list_for_charts.push(permutation);
        }
        isKeyNameListExists = true;
    });
    request.valid_key_list_for_charts = request.valid_key_list_for_charts.slice(0, chart.lines || 3);
}

const _addChartData = (request, reportData, fieldsFilters, chart, filteredRows, reportConfig = {}) => {
    // handle case where table is empty, we want that filters will be empty and therefore the request won't be sent
    const { metadata = {}, results = [] } = reportData || {};
    const chartRefRows = filteredRows || results;
    if (reportData === null || !chartRefRows.length) request.filters = [];

    request.permutation_keys = [];
    request.chart = true;

    // Optimization #2: Auto-adjust time breakdown for long date ranges
    // If user has explicitly selected a chart breakdown, use that
    if (chart.chartTimeBreakdown) {
        request.time_breakdown = [chart.chartTimeBreakdown];
    } else {
        if (request.time_breakdown.includes('all') || request.time_breakdown.length === 0) {
            request.time_breakdown = ['day'];
        }

        if (request.time_breakdown.length > 0) {
            const optimizedBreakdown = getChartTimeBreakdown(
                request.start_date,
                request.end_date,
                request.time_breakdown[0]
            );
            request.time_breakdown = [optimizedBreakdown];
        }
    }

    // Arrange proper filters
    Object.keys(metadata).forEach(fieldName => {
        const field = metadata[fieldName];

        if (field.type === ReportFieldTypes.DIMENSION && !DATE_FIELDS.includes(field.name)) {
            request.permutation_keys.push(field);
        }

        const filterField = fieldsFilters.find(filter => filter.name === field.name);

        if (filterField && !isPostProcessFilterField(filterField)) {
            const filter = {
                dimension: filterField.name,
                operator: IN_OPERATOR,
                values: [],
                options: { no_lowercase: true },
            };
            chartRefRows.forEach(row => {
                const filterValue = filterField.values.find(obj => {
                    let fieldDisplayName = obj.display_name;

                    if (field.name === 'source') {
                        fieldDisplayName = NETWORKS_SPECIAL_ORIGINAL_DISPLAY_NAME_MAPPING[obj.name] || fieldDisplayName;
                    }

                    return fieldDisplayName === row[field.name];
                });

                if (filterValue && filter.values.indexOf(filterValue.name) === -1) {
                    filter.values.push(filterValue.name);
                }
            });

            // Check if filter was already selected, if yes replace it with the proper one.
            const existingFilterIndex = request.filters.findIndex(
                cFilter => cFilter.dimension === filter.dimension && cFilter.operator === filter.operator
            );

            if (filter.values.length > 0) {
                if (existingFilterIndex > -1) {
                    request.filters.splice(existingFilterIndex, 1);
                }

                request.filters.push(filter);
            }
        }
    });

    request.permutation_keys.sort((a, b) => {
        if (a.id < b.id) return -1;
        if (a.id > b.id) return 1;
        return 0;
    });

    _buildValidPermutations(request, chartRefRows, chart);
};

// This selector filters selectedFields by visibility, meaning if a field was selected and is no longer visible it won't be returned.
// This is for guarding cases of fields selected by default but are not visible.
const visibleSelectedFields = reportType => {
    const reportObject = getConfigForType(reportType);
    return createSelector(
        [
            reportObject.selectors.selectedFields,
            getAdminModeEnabled,
            getFields,
            getUserData,
            reportObject.selectors.isSlimMode,
            reportObject.selectors.hasSearchFilter,
        ],
        (selectedFields, adminModeEnabled, fields, userData, isSlimMode, hasSearchFilter) => {
            const ret = {};
            Object.keys(selectedFields).forEach(fieldType => {
                ret[fieldType] = selectedFields[fieldType].filter(sf => {
                    const fieldObj = fields[fieldType].find(f => f.name === sf);
                    return (
                        fieldObj &&
                        reportObject.visibleRule(fieldObj, adminModeEnabled, userData, isSlimMode, hasSearchFilter)
                    );
                });
            });
            return ret;
        }
    );
};

const getQueryDataForType = (reportType, queryType) => {
    const reportConfig = getConfigForType(reportType);

    return createSelector(
        [
            visibleSelectedFields(reportType),
            reportConfig.selectors.selectedFields,
            reportConfig.selectors.formattedFilters,
            reportConfig.selectors.dates,
            reportConfig.selectors.reportData,
            reportConfig.selectors.chart,
            getFilters,
            reportConfig.selectors.selectedGoal,
            reportConfig.selectors.selectedTab,
            reportConfig.selectors.mode,
            getFieldsForType('goals'),
            reportConfig.selectors.unenriched,
            reportConfig.selectors.admonAlignment,
            reportConfig.selectors.displayAlignment,
            reportConfig.selectors.showRedownloadsDimensionInReport,
            reportConfig.selectors.showValidatedDimensionInReport,
            reportConfig.selectors.bookmark,
            getUserData,
            reportConfig.selectors.isSlimMode,
            getFields,
        ],
        (
            visibleSelectedFields,
            selectedFields,
            filters,
            dates,
            reportData,
            chart,
            fieldsFilters,
            selectedGoal,
            selectedTab,
            selectedMode,
            goals,
            unenriched,
            admonAlignment,
            displayAlignment,
            showRedownloadsDimensionInReport,
            showValidatedDimensionInReport,
            bookmark,
            userData,
            is_slim_mode,
            allFields
        ) => {
            const {
                dimensions = [],
                metrics = [],
                discrepancyMetrics: discrepancy_metrics = [],
                cohortMetrics: cohort_metrics = [],
                periods: cohort_periods = [],
                timeBreakdowns = [],
                creativeShowPreview = [],
                sourceAttributionType = [],
                withDruidAppend = [],
                druidAggregatedDataSourcesMode = [],
                crossDeviceDemoCohortType = [],
                crossDeviceCohortType = [],
                skanConversionDimensions: skan_conversion_dimensions = [],
                skan_redownloads_dimensions = [],
                skan_validated_dimensions = [],
                confidenceIntervalFlag = [],
                goalsMetrics: goals_metrics = [],
                goalsForecastMetrics: goals_forecast_metrics = [],
                customEvents = [],
                custom_dimensions = [],
                governanceDimensions = [],
                creativesAIDimensions: creatives_ai_dimensions = [],
                creativesAICustomDimensions: creatives_ai_custom_dimensions = [],
                fileCombinerDimensions: file_combiner_dimensions = [],
                granularityLevels: granularity_levels = [],
                enrichmentDimensions: enrichment_dimensions = [],
                trackerData: tracker_data = [],
                networkData: network_data = [],
                discrepancies = [],
                videoMetrics = [],
                metadataDimensions: metadata_dimensions = [],
                creativeMeasurementDimensions: creative_measurement_dimensions = [],
                socialEngagementMetrics = [],
                assistMetrics = [],
                xorgMetrics = [],
                xorgCohortMetrics = [],
                appMetrics = [],
                webMetrics = [],
                utmParamsFields = [],
                searchCampaignDimensions = [],
                organicInsightsDimensions = [],
                attributionInsightsDimensions = [],
                crossDeviceMetrics = [],
                skanModeledMetrics = [],
                skanModeledCohortMetrics: skan_modeled_cohort_metrics = [],
                skanModeledCohortPeriods: skan_modeled_cohort_periods = [],
                unifiedCohortPeriods: unified_cohort_periods = [],
                conversionEvents = [],
                skanModeledCustomEvents = [],
                adMonDAUMetrics = [],
                skanDateDimensionName,
                skanCustomEvents = [],
                dataTypeFields = [],
                metricGroupFields = [],
                cohortMetricGroupFields = [],
                technicalSpecsDimensions: technical_specs_dimensions = [],
                promotedContentDetailsDimensions: promoted_content_details_dimensions = [],
            } = selectedFields;

            let { start_date = '', end_date = '', start_date_2 = '', end_date_2 = '' } = dates;

            const filteredRows = getGridFilteredData(`report_${reportType}`);
            const time_breakdown = timeBreakdowns;
            const creative_group_by = creativeShowPreview;
            const confidence_interval_flag = reportConfig.confidenceIntervalFlag || confidenceIntervalFlag;
            const skanValidatedDimensions = reportConfig.skanValidatedDimensions || skan_validated_dimensions;
            const source_attribution_type = reportConfig.sourceAttributionType || sourceAttributionType;
            const with_append_tables = reportConfig.withDruidAppend || withDruidAppend;
            const druid_aggregated_data_sources_mode =
                reportConfig.druidAggregatedDataSourcesMode || druidAggregatedDataSourcesMode;
            const cross_device_cohort_type = reportType === reportTypes.crossDevice ? crossDeviceCohortType : [];
            const cross_device_demo_cohort_type =
                reportType === reportTypes.crossDevice ? crossDeviceDemoCohortType : [];
            const is_goals = selectedTab === 'goal';
            const goal =
                selectedGoal && goals && goals.length ? goals.find(cGoal => cGoal.name === selectedGoal) : null;

            if (is_goals && goal) {
                start_date = moment(goal.start_date).format('YYYY-MM-DD');
                end_date = moment(goal.end_date).format('YYYY-MM-DD');
            }

            const api_version = reportConfig.isCreativeExplore ? API_VERSIONS.VERSION_2 : API_VERSIONS.VERSION_1;

            const getSelectedFieldsByType = (fields, selectedFieldNames, type) =>
                fields
                    .filter(field => field.type === type && selectedFieldNames.includes(field.name))
                    .map(field => field.name);

            const isCreativeQuery =
                reportType === reportTypes.creativeExplore ||
                ((reportType === reportTypes.mainReport || reportType === reportTypes.pivot) &&
                    !!creative_measurement_dimensions?.length);

            const preProcessFilters = filters.filter(filter => !filter.isPostProcess);
            const postProcessFilters = filters
                .filter(filter => filter.isPostProcess && filter.values[0]?.trim())
                .map(filter => ({
                    field: filter.dimension,
                    operator: filter.operator,
                    value: filter.values[0].trim(),
                }));

            const request = {
                api_version,
                // disableCompare configs (e.g. retention) aren't compare-safe: never send compare:true.
                compare: !reportConfig.disableCompare && selectedMode === COMPARE_MODE,
                is_goals,
                dimensions: dimensions
                    .concat(custom_dimensions)
                    .concat(skan_conversion_dimensions)
                    .concat(governanceDimensions)
                    .concat(utmParamsFields)
                    .concat(searchCampaignDimensions)
                    .concat(organicInsightsDimensions)
                    .concat(attributionInsightsDimensions)
                    .concat(getSelectedFieldsByType(allFields.trackerData, tracker_data, ReportFieldTypes.DIMENSION))
                    .concat(getSelectedFieldsByType(allFields.networkData, network_data, ReportFieldTypes.DIMENSION)),
                metrics: metrics
                    .concat(xorgMetrics)
                    .concat(appMetrics)
                    .concat(webMetrics)
                    .concat(skanModeledMetrics)
                    .concat(crossDeviceMetrics)
                    .concat(conversionEvents)
                    .concat(adMonDAUMetrics)
                    .concat(socialEngagementMetrics)
                    .concat(assistMetrics)
                    .concat(videoMetrics)
                    .concat(getSelectedFieldsByType(allFields.trackerData, tracker_data, ReportFieldTypes.METRIC))
                    .concat(getSelectedFieldsByType(allFields.networkData, network_data, ReportFieldTypes.METRIC))
                    .concat(discrepancies),
                discrepancy_metrics: reportConfig.discrepancyMetrics || discrepancy_metrics,
                file_combiner_dimensions,
                granularity_levels,
                enrichment_dimensions,
                metadata_dimensions,
                creative_measurement_dimensions,
                promoted_content_details_dimensions,
                skan_conversion_dimensions,
                technical_specs_dimensions,
                creatives_ai_dimensions: creatives_ai_dimensions.concat(creatives_ai_custom_dimensions),
                // Forced-cohort configs send only their resolved set (deduped), ignoring selected
                // cohort fields — hydration could otherwise duplicate or leak unrelated uuids.
                cohort_metrics: reportConfig.getCohortMetrics
                    ? [...new Set(reportConfig.getCohortMetrics(allFields))]
                    : cohort_metrics
                          .concat(customEvents)
                          .concat(xorgCohortMetrics)
                          .concat(skanCustomEvents),
                skan_modeled_cohort_metrics: reportConfig.skanModeledCohortMetrics || skan_modeled_cohort_metrics,
                modeled_skan_custom_events: skanModeledCustomEvents,
                data_type_fields: dataTypeFields,
                metric_group_fields: metricGroupFields,
                cohort_metric_group_fields: cohortMetricGroupFields,
                cohort_periods: reportConfig.cohortPeriods || cohort_periods,
                skan_modeled_cohort_periods: reportConfig.skanModeledCohortPeriods || skan_modeled_cohort_periods,
                unified_cohort_periods: reportConfig.unifiedCohortPeriods || unified_cohort_periods,
                goals_metrics,
                goals_forecast_metrics,
                filters: optimizeFilters(preProcessFilters, fieldsFilters),
                ...(postProcessFilters.length && { post_process_filters: postProcessFilters }),
                time_breakdown,
                creative_group_by,
                skan_date_dimension_name: skanDateDimensionName?.length
                    ? skanDateDimensionName[0]
                    : reportConfig.skanDateDimensionName,
                source_attribution_type,
                with_append_tables,
                druid_aggregated_data_sources_mode,
                cross_device_cohort_type,
                cross_device_demo_cohort_type,
                start_date,
                start_date_2,
                end_date,
                end_date_2,
                goal,
                display_unenriched: unenriched,
                display_admon_alignment: admonAlignment,
                skan_redownloads_dimensions,
                skan_validated_dimensions: skanValidatedDimensions,
                confidence_interval_flag,
                show_redownloads_dimension_in_report: showRedownloadsDimensionInReport,
                show_validated_dimension_in_report: showValidatedDimensionInReport,
                bookmark_id: bookmark.bookmark_id,
                bookmark_creator: bookmark.bookmark_creator,
                updatedInstanceId: bookmark.updatedInstanceId,
                is_fraud: !!reportConfig.isFraud,
                is_skan_summary: !!reportConfig.isSkanSummary,
                is_unified_report: !!reportConfig.isUnifiedReport,
                is_creative_explore: !!reportConfig.isCreativeExplore,
                is_creative_reporting_query: isCreativeQuery,
                is_skan: !!reportConfig.isSkan,
                is_admon_report: !!reportConfig.is_admon_report,
                query_type: queryType,
                is_slim_mode,
            };

            // Set display_alignment based on report type
            if (reportType === reportTypes.creativeExplore) {
                request.display_alignment = displayAlignment;
            } else if (
                (reportType === reportTypes.mainReport || reportType === reportTypes.pivot) &&
                creative_measurement_dimensions?.length
            ) {
                request.display_alignment = false;
            }

            // this is a temporary fix to remove this problematic dimension from existing queries. will be removed soon. let me know if you still see it :)
            request.dimensions = request.dimensions.filter(dim => {
                return dim !== PUBLISHER_ESTIMATED_INDICATION;
            });

            if (reportConfig.requireApiKey) {
                request.api_key = userData.api_key;
            }

            if (queryType === 'charts') {
                _addChartData(request, reportData, fieldsFilters, chart, filteredRows, reportConfig);
            }
            return request;
        }
    );
};

const getIsClearSelectionOn = reportType => {
    return createSelector([getConfigForType(reportType).selectors.isClearSelectionOn], isClearSelectionOn => {
        return isClearSelectionOn;
    });
};

const getReportDataForType = reportType => {
    return createSelector([getConfigForType(reportType).selectors.reportData], reportData => {
        return {
            reportData,
        };
    });
};

const getAnonymousList = reportType => {
    return createSelector([getConfigForType(reportType).selectors.anonymousList], anonymousList => {
        return anonymousList;
    });
};

const getAnonymousId = reportType => {
    return createSelector([getConfigForType(reportType).selectors.anonymousId], anonymousId => {
        return anonymousId;
    });
};

const getTableConfig = reportType => {
    return createSelector([getConfigForType(reportType).selectors.table], table => {
        return table;
    });
};

const getBookmark = reportType => {
    return createSelector([getConfigForType(reportType).selectors.bookmark], bookmark => {
        return bookmark;
    });
};

const getQueryRunning = reportType => {
    return createSelector([getConfigForType(reportType).selectors.queryRunning], isRunning => {
        return isRunning;
    });
};

export const getSelectedSources = reportType => {
    return createSelector([getConfigForType(reportType).selectors.selectedSources], selectedSources => selectedSources);
};

export const getIsAdvanceMode = reportType => {
    return createSelector([getConfigForType(reportType).selectors.isAdvanceMode], isAdvanceMode => isAdvanceMode);
};

const getGoalsDropdown = reportType => {
    return createSelector(
        [getConfigForType(reportType).selectors.selectedGoal, getFieldsForType('goals')],
        (selectedGoal, goals) => {
            let items = [];
            let selected = {
                name: 'default',
                display_name: 'STATIC.PAGES.GOALS.NO_GOALS_TEXT',
            };
            let disabled = true;
            if (goals && goals.length) {
                items = goals.map(goal => ({
                    name: goal.name,
                    display_name: `
                        <span style="display: flex; pointer-events: none;">
                            <span style="padding-right: 5px;">${goal.name} |</span>
                            <span style="margin-left: auto;">${goal.start_date} - ${goal.end_date}</span>
                        </span>
                `,
                }));
                selected = selectedGoal
                    ? items.find(item => item.name === selectedGoal)
                    : {
                          name: 'default2',
                          display_name: 'STATIC.PLACEHOLDERS.SELECT_GOAL',
                      };
                disabled = false;
            }
            return {
                items,
                selected,
                disabled,
            };
        }
    );
};

const getActiveBookmarkName = reportType => {
    return createSelector([getBookmark(reportType), getFieldsForType('bookmarks')], (bookmark = {}, bookmarks = []) => {
        const bookmarkData = bookmarks.find(({ uuid }) => bookmark.bookmark_id === uuid) || {};

        return bookmarkData.name;
    });
};

const getShowPopupDialog = (state, reportType) => {
    return createSelector([], () => {
        return state[reportType].showPopupDialog;
    });
};

export const getReportsFilters = (state, reportType) => {
    return createSelector([], () => {
        return state[reportType].filters;
    });
};

const getPopupDialogParams = (state, reportType) => {
    return createSelector([], () => {
        return state[reportType].popupDialogParams;
    });
};

const getIsEditShelfOpen = (state, reportType) => {
    return createSelector([], () => {
        return state[reportType].isEditShelfOpen;
    });
};

export const getSelectedFields = (state, reportType) => {
    return createSelector([], () => {
        return state[reportType].selectedFields;
    });
};

const getBookmarkPopoverData = reportType => {
    return createSelector(
        [
            getBookmark(reportType),
            getReportDates(reportType),
            getConfigForType(reportType).selectors.mode,
            getFieldsForType('bookmarks'),
        ],
        (bookmark, reportDates, mode, bookmarks) => {
            const {
                start_date: startDate,
                end_date: endDate,
                start_date_2: startDate2,
                end_date_2: endDate2,
            } = reportDates;
            let bookmarkData = null;
            if (bookmarks && bookmarks.length && bookmark && bookmark.bookmark_id) {
                bookmarkData = bookmarks.find(bm => bm.uuid === bookmark.bookmark_id);
            }
            if (!bookmarkData && bookmark && bookmark.bookmark) {
                bookmarkData = bookmark.bookmark;
            }
            return {
                bookmarkId: bookmark.bookmark_id,
                error: bookmark.error,
                bookmarkData,
                endDate,
                startDate,
                endDate2,
                startDate2,
                compareMode: mode === COMPARE_MODE,
                updatedInstanceId: bookmark.updatedInstanceId,
            };
        }
    );
};

// This selector retrieves filters that will match only the results currently queried (i.e., won't take into consideration
// the table filters, but the filters given by the query editor). Used mainly by the anonymous report, so there won't be
// any entries that will be added posteriori
const getFrozenFilters = reportType => {
    function filterValuesFromResults(filter, results) {
        return filter.values.filter(({ display_name, name }) => {
            const resultsFilterName = NETWORKS_SPECIAL_ORIGINAL_DISPLAY_NAME_MAPPING[name] || display_name;
            return (filter.name === 'creative_type'
                ? results.map(result => CreativeTypesMapping[result[filter.name]])
                : results.map(result => result[filter.name])
            ).includes(resultsFilterName);
        });
    }

    return createSelector([getConfigForType(reportType).selectors.reportData, getFilters], (reportData, filters) => {
        const { metadata, results } = reportData;
        const refFilters = filters.filter(f => Object.keys(metadata).includes(f.name));
        return refFilters.map(f =>
            isPostProcessFilterField(f) ? f : { ...f, values: filterValuesFromResults(f, results) }
        );
    });
};

const getReportSpecificProp = (reportType, propName) => {
    return createSelector([getConfigForType(reportType).selectors[propName]], val => val);
};
const getShouldDrilldown = reportType => getReportSpecificProp(reportType, 'shouldDrilldown');
const getChartConfig = reportType => getReportSpecificProp(reportType, 'chart');
const getPostProcessFiltersUI = reportType => getReportSpecificProp(reportType, 'postProcessFiltersUI');

const getReportState = (state, reportType) => state[reportType];

const getSupportedAppNames = createSelector([getReportState], state => state?.reportSupportedApps);
const getUnsupportedAppTooltip = createSelector([getReportState], state => state?.reportUnsupportedAppTooltip);

const reportDataSelectors = {};

// create the main report selectors, per type
reportsConfig.forEach(({ type, dataSelector, selectors }) => {
    const keys = Object.keys(dataSelector);
    reportDataSelectors[type] = createSelector(
        keys.map(selectorName => selectors[selectorName]),
        (...args) => {
            const ret = {};
            for (let i = 0; i < keys.length; i++) {
                ret[dataSelector[keys[i]]] = args[i];
            }
            return ret;
        }
    );
});

const getFieldGroupsToHide = () => {
    return createSelector([getUserData], ({ event_slot_analysis_types: eventSlotAnalysisTypesForOrg = [] }) => {
        return {
            conversionEvents: !showConversionEvents(eventSlotAnalysisTypesForOrg),
            customEvents: !showCohortEvents(eventSlotAnalysisTypesForOrg),
        };
    });
};

const getCustomFieldGroupLabels = () => {
    return createSelector([getUserData], ({ event_slot_analysis_types: eventSlotAnalysisTypesForOrg = [] }) => {
        const customFieldGroupLabels = {};

        if (showConversionEvents(eventSlotAnalysisTypesForOrg)) {
            customFieldGroupLabels.customEvents = 'STATIC.COHORT_EVENTS';
        }

        return customFieldGroupLabels;
    });
};

export {
    getCalcFilterForType,
    getAvailableFiltersForType,
    getWarningForType,
    shouldShowDownloadReport,
    getQueryDataForType,
    getReportDataForType,
    getAnonymousId,
    getAnonymousList,
    getTableConfig,
    getReportDates,
    getBookmark,
    getGoalsDropdown,
    reportDataSelectors,
    getBookmarkPopoverData,
    getTableFormattedData,
    getQueryRunning,
    getConfigForType,
    getFrozenFilters,
    getShouldDrilldown,
    getChartConfig,
    getAvailableDimensionsForDrilldown,
    getPostProcessFiltersUI,
    getIsClearSelectionOn,
    getCustomFieldGroupLabels,
    getFieldGroupsToHide,
    getActiveBookmarkName,
    getShowPopupDialog,
    getPopupDialogParams,
    getShelfShown,
    getIsEditShelfOpen,
    getReportState,
    getSupportedAppNames,
    getUnsupportedAppTooltip,
    getChartTimeBreakdown,
    CHART_OPTIMIZATION_DATE_RANGES,
    TIME_BREAKDOWNS,
};
